import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { toast } from "../ui/sonner";
import { StatusBadge } from "./StatusBadge";
import { ConfirmDialog } from "./ConfirmDialog";
import { formatDate } from "../../lib/dateUtils";

export const SessionsTab = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(null);

  const load = () => {
    api.get("/admin/sessions").then(({ data }) => setSessions(data));
  };

  useEffect(() => {
    load();
  }, []);

  const cancelSession = async (id) => {
    try {
      await api.put(`/admin/sessions/${id}/cancel`);
      toast.success("Session cancelled");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't cancel session");
    }
  };

  const approveSession = async (id) => {
    try {
      await api.put(`/admin/sessions/${id}/approve`);
      toast.success("Session approved and now live");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't approve session");
    }
  };

  if (sessions === null) return <p className="font-medium text-midnight-black/60">Loading sessions...</p>;

  return (
    <div data-testid="admin-sessions-tab">
      <div className="flex justify-end mb-4">
        <Button data-testid="create-session-button" onClick={() => navigate("/admin/sessions/new")} className="bg-stranger-orange hover:bg-orange-600 text-clean-white font-bold rounded-full text-xs gap-2">
          <Plus className="w-3.5 h-3.5" /> CREATE SESSION
        </Button>
      </div>
      {sessions.length === 0 ? (
        <p className="font-medium text-midnight-black/60" data-testid="no-admin-sessions">No sessions yet.</p>
      ) : (
        <div className="space-y-3" data-testid="admin-sessions-list">
          {sessions.map((s) => (
            <div key={s.id} className="border border-soft-gray rounded-xl p-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`admin-session-row-${s.id}`}>
              <div className="cursor-pointer flex-1 min-w-[200px]" onClick={() => navigate(`/admin/sessions/${s.id}`)} data-testid={`admin-session-row-link-${s.id}`}>
                <p className="font-extrabold text-sm uppercase flex items-center gap-2">
                  {s.title}
                  <StatusBadge status={s.status} testId={`admin-session-status-${s.id}`} />
                </p>
                <p className="text-xs font-medium text-midnight-black/60">
                  {s.photographer_name || "Unknown photographer"} · {s.city}, {s.state} · {formatDate(s.session_date)}
                </p>
                <p className="text-xs font-medium text-midnight-black/50">{s.registrations_count}/{s.capacity} registered</p>
              </div>
              {s.status !== "cancelled" && (
                <div className="flex items-center gap-2">
                  {s.status === "pending_review" && (
                    <Button data-testid={`admin-approve-session-${s.id}`} onClick={() => approveSession(s.id)} className="bg-stranger-orange hover:bg-orange-600 text-clean-white rounded-full font-bold text-xs">
                      APPROVE
                    </Button>
                  )}
                  <Button variant="outline" data-testid={`admin-edit-session-${s.id}`} onClick={() => navigate(`/admin/sessions/${s.id}/edit`)} className="rounded-full font-bold text-xs">
                    EDIT
                  </Button>
                  <Button variant="outline" data-testid={`admin-cancel-session-${s.id}`} onClick={() => setConfirmCancel(s.id)} className="rounded-full font-bold text-xs">
                    CANCEL SESSION
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!confirmCancel}
        onOpenChange={(v) => !v && setConfirmCancel(null)}
        title="Cancel this session?"
        description="All active registrations for this session will also be cancelled. This action is logged."
        confirmLabel="CANCEL SESSION"
        destructive
        onConfirm={() => cancelSession(confirmCancel)}
      />
    </div>
  );
};
